// SyberKey MFA Service for MaxLife SSO Portal
// This service handles the integration with SyberKey MFA after Cognito authentication

export interface SyberKeyMfaConfig {
  apiBase: string;
  tenantId: string;
  bankName: string;
  source: string;
}

export interface AuthInitiateRequest {
  userId: string;
  bankName: string;
  source: string;
  tenant: string;
}

export interface AuthInitiateResponse {
  success: boolean;
  status: string;
  loginId: string;
  userId: string;
  expiresIn: number;
  expiresAt: number;
  message: string;
}

export interface SessionStatusResponse {
  status: 'pending' | 'approved' | 'rejected' | 'denied' | 'expired';
  loginId: string;
  userId: string;
  tenantId?: string;
  message?: string;
}

class SyberKeyMfaService {
  private config: SyberKeyMfaConfig;

  constructor() {
    this.config = {
      // API Service - use production URL by default, localhost for development
      apiBase: process.env.REACT_APP_SYBERKEY_API_BASE || process.env.REACT_APP_API_BASE_URL || 
        (typeof window !== 'undefined' && 
         (window.location.hostname === 'sso.syberkey.com' || !window.location.hostname.includes('localhost'))
         ? 'https://api.syberkey.com' 
         : 'http://localhost:3000'),
      tenantId: process.env.REACT_APP_SYBERKEY_TENANT_ID || 'maxlife_26cc36',
      bankName: 'MaxLife Corporation',
      source: 'maxlife_sso_portal'
    };
  }

  /**
   * Check if user exists and is mapped to tenant
   */
  async checkUserTenantMapping(email: string): Promise<{
    success: boolean;
    syberkeyUserId?: string;
    qrBlob?: string;
    error?: string;
    message?: string;
  }> {
    try {
      console.log(`Checking tenant mapping for email: ${email}, tenant: ${this.config.tenantId}`);

      const response = await fetch(`${this.config.apiBase}/user-tenant-mapping/tenant/${this.config.tenantId}/user/email/${email}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('User tenant mapping check failed:', errorData);
        
        if (errorData.error === 'user_not_found') {
          return {
            success: false,
            error: 'USER_NOT_FOUND',
            message: 'User not found or has not shared credentials with MaxLife Corporation. Please visit https://appdemo.syberkey.com/ to register and share your credentials with MaxLife Corporation.'
          };
        }
        
        return {
          success: false,
          error: errorData.error || 'MAPPING_ERROR',
          message: errorData.message || 'User has not shared credentials with this tenant.'
        };
      }

      const result = await response.json();
      console.log('User tenant mapping check successful:', result);

      if (result.success) {
        return {
          success: true,
          syberkeyUserId: result.userId,
          qrBlob: result.qrBlob || '',
          message: 'User has shared credentials with MaxLife Corporation'
        };
      } else {
        return {
          success: false,
          error: result.error || 'MAPPING_FAILED',
          message: result.message || 'User has not shared credentials with this tenant.'
        };
      }

    } catch (error) {
      console.error('User tenant mapping check error:', error);
      return {
        success: false,
        error: 'NETWORK_ERROR',
        message: 'Unable to connect to SyberKey service. Please try again later.'
      };
    }
  }

  /**
   * Initiate SyberKey MFA authentication
   */
  async initiateAuth(userId: string): Promise<AuthInitiateResponse> {
    try {
      const requestBody: AuthInitiateRequest = {
        userId,
        bankName: this.config.bankName,
        source: this.config.source,
        tenant: this.config.tenantId
      };

      console.log('🚀 [SyberKeyMfaService] Initiating SyberKey MFA auth:', requestBody);
      console.log('🚀 [SyberKeyMfaService] API URL:', `${this.config.apiBase}/auth/initiate`);

      const response = await fetch(`${this.config.apiBase}/auth/initiate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('SyberKey auth initiation failed:', errorData);
        throw new Error(errorData.message || `Failed to initiate SyberKey authentication: ${response.status}`);
      }

      const result = await response.json();
      console.log('SyberKey auth initiated successfully:', result);
      return result;

    } catch (error) {
      console.error('SyberKey MFA initiation error:', error);
      throw error;
    }
  }

  /**
   * Check the status of a SyberKey MFA session
   */
  async checkSessionStatus(loginId: string): Promise<SessionStatusResponse> {
    try {
      console.log('Checking session status for loginId:', loginId);

      const response = await fetch(`${this.config.apiBase}/sessions/status/${loginId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Session status check failed:', errorData);
        throw new Error(errorData.message || `Failed to check session status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Session status:', result);
      return result;

    } catch (error) {
      console.error('Session status check error:', error);
      throw error;
    }
  }

  /**
   * Poll for session status with timeout
   */
  async pollForApproval(
    loginId: string,
    onStatusUpdate: (status: SessionStatusResponse) => void,
    onError: (error: Error) => void,
    options: {
      maxAttempts?: number;
      intervalMs?: number;
      maxConsecutiveErrors?: number;
    } = {}
  ): Promise<void> {
    const {
      maxAttempts = 60, // 5 minutes with 5-second intervals
      intervalMs = 5000,
      maxConsecutiveErrors = 3
    } = options;

    let attempts = 0;
    let consecutiveErrors = 0;

    const poll = async (): Promise<void> => {
      try {
        const status = await this.checkSessionStatus(loginId);
        consecutiveErrors = 0; // Reset error counter on successful response

        onStatusUpdate(status);

        // Check if we should stop polling
        if (status.status === 'approved' || status.status === 'rejected' || status.status === 'denied') {
          return;
        }

        // Continue polling if not at max attempts
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, intervalMs);
        } else {
          onError(new Error('Authentication timeout. Please try again.'));
        }

      } catch (error) {
        console.error('Polling error:', error);
        consecutiveErrors++;

        // Check if we should stop due to too many consecutive errors
        if (consecutiveErrors >= maxConsecutiveErrors) {
          onError(new Error('Too many consecutive errors. Please try again.'));
          return;
        }

        // Continue polling if not at max attempts
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, intervalMs);
        } else {
          onError(new Error('Authentication timeout due to errors. Please try again.'));
        }
      }
    };

    // Start polling after a short delay
    setTimeout(poll, 2000);
  }

  /**
   * Get the SyberKey mobile app URL
   */
  getMobileAppUrl(): string {
    return 'https://appdemo.syberkey.com/dashboard';
  }

  /**
   * Open SyberKey mobile app in new tab
   */
  openMobileApp(): void {
    window.open(this.getMobileAppUrl(), '_blank');
  }

  /**
   * Get configuration
   */
  getConfig(): SyberKeyMfaConfig {
    return { ...this.config };
  }
}

// Export singleton instance
export const syberKeyMfaService = new SyberKeyMfaService();
export default syberKeyMfaService;
